/**
 * `CountryFilter` — dropdown that selects the `?country=<…>` segment
 * for the cohort retention grid. Options are derived from the
 * overview's `geography` array so the picker only ever offers
 * countries the server has data for; `'all'` is the default + first
 * option, `'ZZ'` ("Unknown") is included when present.
 *
 * The control is intentionally a native `<select>`: the country list
 * can run to ~20 items (server cap), a custom popover would be
 * over-engineering for an investor-grade dashboard, and the native
 * widget gets keyboard + accessibility for free.
 *
 * Visual: mono uppercase kicker label sits next to the select; the
 * select chrome is themed to match the surrounding mono-bordered
 * controls (`RangePresets`) so the two filters read as a single row.
 */

import { ChevronDown } from 'lucide-react';

import type { AdminMetricsGeographyRow } from '@sill/types';

import { countryName } from '../../lib/country-names.js';
import { useC } from '../../lib/theme.js';

const monoFont = "'JetBrains Mono', monospace";

/**
 * Resolved-filter sentinel string. `'all'` means "ungrouped grid";
 * any other value is an ISO-3166-1 alpha-2 OR the literal `'ZZ'`.
 *
 * Mirrors `AdminMetricsCohorts.country` on the wire (`'all' | string`).
 */
export type CountryFilterValue = string;

export interface CountryFilterProps {
  /** Currently-selected segment. `'all'` is the default. */
  value: CountryFilterValue;
  /** Available countries — typically the overview's `geography` array.
   *  Empty array → only "All" is offered. */
  countries: ReadonlyArray<AdminMetricsGeographyRow>;
  onChange: (next: CountryFilterValue) => void;
  /** Disables the control during fetches. */
  disabled?: boolean;
}

export function CountryFilter({
  value,
  countries,
  onChange,
  disabled,
}: CountryFilterProps): JSX.Element {
  const C = useC();

  // Build the option list: `all` first, then every country the server
  // included (already sorted by active_accounts DESC). The `'ZZ'`
  // bucket is rendered as "Unknown" — the call site reads the resolved
  // server value back into the section header so the staff member sees
  // exactly which segment they're looking at.
  const options: ReadonlyArray<{ value: string; label: string }> = [
    { value: 'all', label: 'All countries' },
    ...countries.map((row) => ({
      value: row.geo_country,
      label: countryName(row.geo_country),
    })),
  ];

  return (
    <label
      className="inline-flex items-center"
      style={{ gap: 8 }}
    >
      <span
        style={{
          fontFamily: monoFont,
          fontSize: 9,
          color: C.muted,
          letterSpacing: 2,
          textTransform: 'uppercase',
        }}
      >
        Country
      </span>
      <div
        className="inline-flex items-center"
        style={{
          position: 'relative',
          background: C.surface2,
          border: `1px solid ${C.border}`,
          borderRadius: 3,
          padding: '2px 2px 2px 10px',
          opacity: disabled === true ? 0.6 : 1,
        }}
      >
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          aria-label="Cohort country filter"
          style={{
            fontFamily: monoFont,
            fontSize: 11,
            letterSpacing: 1,
            textTransform: 'uppercase',
            color: value === 'all' ? C.text : C.limeFg,
            background: 'transparent',
            border: 'none',
            outline: 'none',
            paddingRight: 22,
            cursor: disabled === true ? 'default' : 'pointer',
            appearance: 'none',
            WebkitAppearance: 'none',
            MozAppearance: 'none',
          }}
        >
          {options.map((opt) => (
            <option
              key={opt.value}
              value={opt.value}
              style={{
                // Native option styling is browser-controlled; we set
                // a sensible default for browsers that respect it
                // (Firefox), and accept Chrome's system styling for
                // the dropdown panel.
                background: C.surface,
                color: C.text,
              }}
            >
              {opt.label}
              {opt.value !== 'all' ? ` · ${opt.value}` : ''}
            </option>
          ))}
        </select>
        <ChevronDown
          size={12}
          color={C.muted}
          style={{
            position: 'absolute',
            right: 6,
            top: '50%',
            transform: 'translateY(-50%)',
            pointerEvents: 'none',
          }}
          aria-hidden="true"
        />
      </div>
    </label>
  );
}
